import PropTypes from "prop-types";
import { forwardRef, useRef, useState, useMemo, useCallback, useEffect } from "react";
import { FormBuilder } from "@repo/design-system/components/forms";
import "./PageHeader.scss";
import { useTranslation } from "react-i18next";
import { PageHeaderConfig } from "./PageConfig.data";
import { PageHeaderFilterVariant } from "./enum/PageHeaderEnum";
import { ButtonGroup, Iconify, ButtonVariant, Button } from "@repo/design-system/components/ui";

// Keep in sync with $ph-filter-card-duration in PageHeader.scss
const FILTER_CARD_ANIMATION_MS = 350;

const isEmptyFilterValue = (value) => {
    if (value === null || value === undefined || value === "") return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object" && "min" in value) return value.min === undefined && value.max === undefined;
    return false;
};

// Every field of a FormBuilder config keyed by its id / dataField (fields can sit in a section or in a rowGroup)
const getFilterFields = (config) => {
    const fieldMap = {};
    config?.section?.forEach((section) => {
        [...(section.fields ?? []), ...(section.rowGroup ?? []).flatMap((row) => row.fields ?? [])]
            .forEach((field) => {
                if (field?.id) fieldMap[field.id] = field;
                if (field?.dataField) fieldMap[field.dataField] = field;
            });
    });
    return fieldMap;
};

// Display text for an applied value — selects keep { value, label }, plain ids are looked up in the field's
// options (grouped options carry their own options list), ranges show as "min - max"
const getFilterValueLabel = (value, field) => {
    if (Array.isArray(value)) return value.map((item) => getFilterValueLabel(item, field)).join(", ");
    if (typeof value === "object") {
        if ("label" in value) return value.label;
        if ("min" in value) return `${value.min ?? ""} - ${value.max ?? ""}`;
        return "";
    }
    const options = (field?.fieldSetting?.options ?? []).flatMap((option) => option?.options ?? [option]);
    return options.find((option) => String(option?.value) === String(value))?.label ?? String(value);
};

export const PageHeader = forwardRef(({
    title, description, headerIcon, children,
    searchConfig = {},
    filterConfig = {},
    exportConfig = {},
    customButton = [],
    rightContent,
}, ref) => {

    const formRef = useRef();
    const filterRef = useRef();
    const { t } = useTranslation();
    const [filters, setFilters] = useState();
    const [searchData, setSearchData] = useState({});
    // The "card" filter starts collapsed, the default panel starts open
    const [showFilterPanel, setShowFilterPanel] = useState(
        filterConfig?.defaultFilterOpen ?? filterConfig?.variant !== PageHeaderFilterVariant.CARD
    );

    // Card body animation: closed → mounted (collapsed) → expanding → open, and open → leaving → closed.
    // The form stays mounted while it collapses, and overflow is only released once it is fully open
    // so select menus are not clipped
    const [filterBodyState, setFilterBodyState] = useState(showFilterPanel ? "open" : "closed");
    const filterBodyTimers = useRef([]);

    useEffect(() => {
        const timers = filterBodyTimers.current;
        const clearTimers = () => {
            timers.forEach(({ type, id }) => type === "frame" ? cancelAnimationFrame(id) : clearTimeout(id));
            timers.length = 0;
        };
        clearTimers();

        if (showFilterPanel) {
            setFilterBodyState((prev) => prev === "open" ? prev : "mounted");
            // Two frames so the collapsed state is painted before expanding
            const outer = requestAnimationFrame(() => {
                const inner = requestAnimationFrame(() => {
                    setFilterBodyState((prev) => prev === "open" ? prev : "expanding");
                    timers.push({ type: "timeout", id: setTimeout(() => setFilterBodyState("open"), FILTER_CARD_ANIMATION_MS) });
                });
                timers.push({ type: "frame", id: inner });
            });
            timers.push({ type: "frame", id: outer });
        } else {
            setFilterBodyState((prev) => prev === "closed" ? prev : "leaving");
            timers.push({ type: "timeout", id: setTimeout(() => setFilterBodyState("closed"), FILTER_CARD_ANIMATION_MS) });
        }
        return clearTimers;
    }, [showFilterPanel]);

    const [showSearchBar, setShowSearchBar] = useState(false); // mobile search toggle

    const {
        isEnabledExport = false,
        onExport,
        exportLabel = "",
        isLoading: isExportLoading = false,
        exportOptions = [],   // [{ label, icon, onClick }]
        ...exportOtherConfig
    } = exportConfig;

    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef();

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!showExportMenu) return;
        const handler = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showExportMenu]);

    const {
        filterFormConfig = [], isEnabledFilter = false, onFilterSearch,
        filterPanelSectionClass = "col-lg-4 col-md-5",
        filterbuttonConfig = {},
        variant: filterVariant = PageHeaderFilterVariant.PANEL,
        filterTitle = "Advanced Filter",
    } = filterConfig;
    const isFilterCard = filterVariant === PageHeaderFilterVariant.CARD;
    const { onSearch, searchFormConfig = [], isEnabledSearch = false } = searchConfig;

    //  Merge default config + external config + state
    const headerConfig = useMemo(() => {
        const baseConfig = PageHeaderConfig(t);

        return {
            ...baseConfig,
            ...searchFormConfig, // allow override from props
            initialState: {
                ...baseConfig.initialState,
                ...(searchFormConfig?.initialState || {}),
                ...searchData,
            },
        };
    }, [t, searchFormConfig, searchData]);

    //  Merge default config + external config + state
    const filterFormConfiguration = useMemo(() => {
        if (isEnabledFilter) {
            const baseConfig = filterFormConfig?.(t);

            return {
                ...baseConfig,
                initialState: {
                    ...baseConfig.initialState,
                    ...filters,
                },
            };
        }
    }, [t, filterFormConfig, filters]);

    // Handle Search
    const handleSearch = useCallback(() => {
        if (!formRef.current) return;

        const formData = formRef.current.getValidatedFormData?.();
        if (!formData) return;

        setSearchData(formData);
        onSearch?.(formData);
    }, [onSearch]);

    // Handle Reset
    const handleSearchReset = useCallback(() => {
        setSearchData({});
        onSearch?.({});
    }, [onSearch]);

    const handleFilterSearch = useCallback(() => {
        if (!filterRef.current) return;
        const formData = filterRef.current.getValidatedFormData?.();
        if (!formData) return;
        setFilters(formData);
        onFilterSearch?.(formData);
    }, [onFilterSearch]);

    const handleFilterRest = useCallback(() => {
        setFilters({});
        onFilterSearch?.({});
        // The card closes on Cancel / Reset, like the design
        if (isFilterCard) setShowFilterPanel(false);
    }, [onFilterSearch, isFilterCard]);

    // Applied filters shown as removable chips under the card
    const appliedFilterChips = useMemo(() => {
        if (!isFilterCard || !filters) return [];
        const fieldMap = getFilterFields(filterFormConfiguration);
        return Object.entries(filters)
            .filter(([key, value]) => fieldMap[key] && !isEmptyFilterValue(value))
            .map(([key, value]) => ({
                key,
                label: fieldMap[key].label || fieldMap[key].Field_Name || key,
                value: getFilterValueLabel(value, fieldMap[key]),
            }))
            .filter((chip) => chip.value);
    }, [isFilterCard, filters, filterFormConfiguration]);

    // Removing a chip re-runs the search without that field — same as clearing it in the form and applying
    const handleRemoveFilterChip = useCallback((key) => {
        const nextFilters = { ...filters, [key]: "" };
        setFilters(nextFilters);
        onFilterSearch?.(nextFilters);
    }, [filters, onFilterSearch]);

    const filterButtons = isEnabledFilter && (
        <ButtonGroup
            sectionClass={isFilterCard ? "ph-filter-card-buttons" : `cmt-14 ${filterPanelSectionClass} justify-content-start`}
            primaryBtnObj={{
                leftIcon: filterbuttonConfig.primaryBtnObj.leftIcon || "charm:tick",
                ...filterbuttonConfig.primaryBtnObj,
                onClick: handleFilterSearch,
            }}
            secondaryBtnObj={{
                leftIcon: filterbuttonConfig.secondaryBtnObj.leftIcon || "material-symbols:close-rounded",
                variant: filterbuttonConfig.secondaryBtnObj.variant
                    || (ButtonVariant.OUTLINE),
                ...filterbuttonConfig?.secondaryBtnObj,
                onClick: handleFilterRest,
            }}
        />
    );

    return (
        <div className="page-header" ref={ref}>
            <div className="page-header-section">

                <div className="page-header-left-info">
                    {headerIcon && (
                        <Iconify className="font-icon" icon={headerIcon} />
                    )}
                    <div className="page-header-text">
                        {title && <h3 className="page-header-title">{title}</h3>}
                        {description && <p className="page-header-description">{description}</p>}
                    </div>
                </div>

                <div className="page-header-right-controls">
                    {isEnabledSearch && (
                        <div className="page-header-search">
                            <FormBuilder ref={formRef} config={headerConfig} />
                            <ButtonGroup
                                primaryBtnObj={{
                                    label: t("Search"),
                                    leftIcon: "icon-park-outline:search",
                                    variant: ButtonVariant.PRIMARY,
                                    onClick: handleSearch,
                                }}
                                secondaryBtnObj={{
                                    label: t("Reset"),
                                    leftIcon: "material-symbols:close-rounded",
                                    variant: ButtonVariant.OUTLINE,
                                    onClick: handleSearchReset,
                                }}
                            />
                        </div>
                    )}

                    {isEnabledExport && (
                        <div className="page-header-export" ref={exportMenuRef}>
                            <Button variant={ButtonVariant.OUTLINE}
                                // iconWidth="18px"
                                leftIcon="material-symbols-light:file-export-rounded"
                                loading={isExportLoading}
                                {...exportOtherConfig}
                                onClick={exportOptions.length > 0
                                    ? () => setShowExportMenu((prev) => !prev)
                                    : onExport
                                }
                                rightIcon={exportOptions.length > 0 ? "mdi:chevron-down" : undefined}>
                                {exportLabel}
                            </Button>

                            {exportOptions.length > 0 && showExportMenu && (
                                <div className="export-dropdown">
                                    {exportOptions.map((option, idx) => (
                                        <button key={idx} className="export-dropdown-item"
                                            onClick={() => {
                                                option.onClick?.();
                                                setShowExportMenu(false);
                                            }}>
                                            {option.icon && (
                                                <Iconify icon={option.icon}
                                                    width={16}
                                                    className="export-dropdown-icon" />
                                            )}
                                            <span>{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {isEnabledFilter && !isFilterCard && (
                        <div className="page-header-filter">
                            <Button
                                className={`page-header-filter-btn ${showFilterPanel ? "active" : ""}`}
                                variant={ButtonVariant.PRIMARY}
                                ttLabel="Filter"
                                targetId="filter"
                                onClick={() => setShowFilterPanel((prev) => !prev)}
                                rightIcon="mdi:filter"
                                iconWidth={22}
                            />
                        </div>
                    )}

                    {customButton?.length > 0 && (
                        <div className="page-header-custom-btn">
                            {customButton.map((btn, index) => (
                                <Button
                                    key={index}
                                    className={`page-header-custom-btn-item ${btn?.className ?? ""}`}
                                    variant={btn?.variant}
                                    ttLabel={btn?.ttLabel}
                                    targetId={btn?.targetId}
                                    onClick={btn?.onClick}
                                    leftIcon={btn?.leftIcon}
                                    size={btn?.size}
                                    loading={btn?.loading}
                                    rightIcon={btn?.rightIcon}
                                    iconWidth={btn?.iconWidth}
                                >
                                    {btn?.label}
                                </Button>
                            ))}
                        </div>
                    )}

                    {rightContent}
                </div>
            </div>

            {showFilterPanel && isEnabledFilter && !isFilterCard &&
                <div className="ph-filter-panel-section">
                    <div className="row align-items-start">
                        <FormBuilder ref={filterRef} config={filterFormConfiguration} />
                        {filterButtons}
                    </div>
                </div>
            }

            {isEnabledFilter && isFilterCard && (
                <div className="ph-filter-card">
                    <div className="ph-filter-card-top">
                        <div className="ph-filter-card-title">
                            <span className="ph-filter-card-icon">
                                <Iconify icon="mdi:filter" width={16} height={16} />
                            </span>
                            {t(filterTitle)}
                        </div>
                        <button type="button"
                            className={`ph-filter-card-toggle ${showFilterPanel ? "open" : ""}`}
                            aria-expanded={showFilterPanel}
                            onClick={() => setShowFilterPanel((prev) => !prev)}>
                            <Iconify icon="lucide:list-filter" width={16} height={16} />
                            {t("Filter")}
                            {appliedFilterChips.length > 0 && (
                                <span className="ph-filter-card-count">{appliedFilterChips.length}</span>
                            )}
                            <Iconify className="ph-filter-card-chevron" icon="lucide:chevron-down" width={14} height={14} />
                        </button>
                    </div>

                    {filterBodyState !== "closed" && (
                        <div className={`ph-filter-card-collapse${filterBodyState === "expanding" || filterBodyState === "open" ? " open" : ""}${filterBodyState === "open" ? " settled" : ""}`}>
                            <div className="ph-filter-card-collapse-inner">
                                <div className="ph-filter-card-body">
                                    <div className="row align-items-start">
                                        <FormBuilder ref={filterRef} config={filterFormConfiguration} />
                                    </div>
                                    <div className="ph-filter-card-actions">
                                        {/* <span className="ph-filter-card-hint">
                                            {appliedFilterChips.length > 0
                                                ? <><b>{appliedFilterChips.length}</b> {t(appliedFilterChips.length === 1 ? "filter applied" : "filters applied")}</>
                                                : t("Showing all records")}
                                        </span> */}
                                        {filterButtons}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* {appliedFilterChips.length > 0 && (
                        <div className="ph-filter-card-chips">
                            {appliedFilterChips.map((chip) => (
                                <span key={chip.key} className="ph-filter-chip">
                                    <b>{t(chip.label)}:</b> {chip.value}
                                    <button type="button" title={t("Remove")}
                                        onClick={() => handleRemoveFilterChip(chip.key)}>
                                        <Iconify icon="material-symbols:close-rounded" width={11} height={11} />
                                    </button>
                                </span>
                            ))}
                            {appliedFilterChips.length > 1 && (
                                <button type="button" className="ph-filter-chip-clear" onClick={handleFilterRest}>
                                    {t("Clear all")}
                                </button>
                            )}
                        </div>
                    )} */}
                </div>
            )}

            {children && (
                <div className="page-header-children">{children}</div>
            )}
        </div>
    );
});

PageHeader.propTypes = {
    title: PropTypes.string.isRequired,
    headerIcon: PropTypes.string,
    children: PropTypes.node,
    searchConfig: PropTypes.shape({
        isEnabledSearch: PropTypes.bool,
        searchFormConfig: PropTypes.object,
        onSearch: PropTypes.func,
    }),
    filterConfig: PropTypes.shape({
        isEnabledFilter: PropTypes.bool,
        filterFormConfig: PropTypes.func,
        onFilterSearch: PropTypes.func,
        filterbuttonConfig: PropTypes.object,
        variant: PropTypes.oneOf(Object.values(PageHeaderFilterVariant)),
        filterTitle: PropTypes.string,
        defaultFilterOpen: PropTypes.bool,
    }),
    exportConfig: PropTypes.shape({
        isEnabledExport: PropTypes.bool,
        onExport: PropTypes.func,
        exportLabel: PropTypes.string,
        isLoading: PropTypes.bool,
        exportOptions: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                icon: PropTypes.string,
                onClick: PropTypes.func,
            })
        ),
    }),
    customButton: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            variant: PropTypes.string,
            ttLabel: PropTypes.string,
            targetId: PropTypes.string,
            onClick: PropTypes.func,
            leftIcon: PropTypes.string,
            rightIcon: PropTypes.string,
            iconWidth: PropTypes.number,
            size: PropTypes.string,
            loading: PropTypes.bool,
            className: PropTypes.string,
        })
    ),
};